<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import CopilotContainer from '../../copilot/CopilotContainer.vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from '../../../featureFlags';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const emit = defineEmits(['toggleContactPanel']);

const { t } = useI18n();

const channelType = computed(() => props.currentChat?.meta?.channel || '');

const CONTACT_TABS_OPTIONS = [
  { key: 'CONTACT', value: 'contact' },
  { key: 'COPILOT', value: 'copilot' },
];

const tabs = computed(() => {
  return CONTACT_TABS_OPTIONS.map(tab => ({
    label: t(`CONVERSATION.SIDEBAR.${tab.key}`),
    value: tab.value,
  }));
});
const activeTab = ref(0);
const toggleContactPanel = () => {
  emit('toggleContactPanel');
};

const handleTabChange = selectedTab => {
  activeTab.value = tabs.value.findIndex(
    tabItem => tabItem.value === selectedTab.value
  );
};
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showCopilotTab = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.CAPTAIN)
);

// Handled here rather than in ContactPanel: the Copilot tab unmounts ContactPanel,
// so the Contact tab has to be restored before the panel can be asked to scroll.
const contactPanelRef = ref(null);
const onScrollToPreviousConversations = () => {
  activeTab.value = 0;
  nextTick(() => contactPanelRef.value?.scrollToPreviousConversations());
};

onMounted(() =>
  emitter.on(
    BUS_EVENTS.SCROLL_TO_PREVIOUS_CONVERSATIONS,
    onScrollToPreviousConversations
  )
);
onUnmounted(() =>
  emitter.off(
    BUS_EVENTS.SCROLL_TO_PREVIOUS_CONVERSATIONS,
    onScrollToPreviousConversations
  )
);
</script>

<template>
  <!-- Phones: an overlay over the conversation, closed by tapping beside it -->
  <div
    class="fixed inset-0 z-30 bg-modal-backdrop-light dark:bg-modal-backdrop-dark md:hidden"
    @click="toggleContactPanel"
  />
  <div
    class="ltr:border-l rtl:border-r border-n-weak h-full overflow-hidden flex flex-col bg-n-background fixed top-0 ltr:right-0 rtl:left-0 z-40 w-[85%] max-w-sm shadow-lg md:static md:z-10 md:shadow-none md:max-w-none md:w-80 md:min-w-80 2xl:min-w-96 2xl:w-96"
  >
    <div class="flex justify-end px-2 pt-2 md:hidden">
      <Button
        icon="i-lucide-x"
        slate
        ghost
        sm
        :aria-label="t('GENERAL.CLOSE')"
        @click="toggleContactPanel"
      />
    </div>
    <div v-if="showCopilotTab" class="p-2">
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTab"
        class="w-full [&>button]:w-full"
        @tab-changed="handleTabChange"
      />
    </div>
    <div class="flex flex-1 overflow-auto">
      <ContactPanel
        v-if="!activeTab"
        ref="contactPanelRef"
        :conversation-id="currentChat.id"
        :inbox-id="currentChat.inbox_id"
        :on-toggle="toggleContactPanel"
      />
      <CopilotContainer
        v-else-if="activeTab === 1 && showCopilotTab"
        :key="currentChat.id"
        :conversation-inbox-type="channelType"
        :conversation-id="currentChat.id"
        class="flex-1"
      />
    </div>
  </div>
</template>
